import React, { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Play, AlertTriangle, CheckCircle2, RefreshCw, Send } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "sonner";

const PRIORITY_STYLE = {
  P0: "bg-red-100 text-red-800 border-red-200",
  P1: "bg-amber-100 text-amber-900 border-amber-200",
  P2: "bg-slate-100 text-slate-700 border-slate-200",
};

const Card = ({ label, value, hint, testid }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testid}>
    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</div>
    <div className="text-2xl font-bold text-navy mt-1">{value}</div>
    {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
  </div>
);

export default function SeoAuditPanel() {
  const [audit, setAudit] = useState(null);
  const [runs, setRuns] = useState([]);
  const [status, setStatus] = useState({ status: "idle" });
  const [priority, setPriority] = useState("");
  const [code, setCode] = useState("");
  const [origin, setOrigin] = useState("");
  const [compare, setCompare] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [indexnow, setIndexnow] = useState(null);
  const [loading, setLoading] = useState(true);
  const poll = useRef(null);

  const loadAudit = useCallback(async () => {
    const { data } = await api.get("/admin/seo-audit/latest", { params: { priority, code } });
    setAudit(data.audit);
    setRuns(data.runs || []);
    setLoading(false);
  }, [priority, code]);

  const loadRest = useCallback(async () => {
    const [cmp, cov, idx] = await Promise.all([
      api.get("/admin/seo-audit/compare").then((r) => r.data).catch(() => null),
      api.get("/admin/prerender/coverage").then((r) => r.data).catch(() => null),
      api.get("/admin/indexnow/status").then((r) => r.data).catch(() => null),
    ]);
    setCompare(cmp);
    setCoverage(cov);
    setIndexnow(idx);
  }, []);

  useEffect(() => { loadAudit(); }, [loadAudit]);
  useEffect(() => { loadRest(); }, [loadRest]);

  useEffect(() => () => poll.current && clearInterval(poll.current), []);

  const startRun = async () => {
    const { data } = await api.post("/admin/seo-audit/run", { origin: origin.trim() });
    if (!data.started) {
      toast.info(data.reason || "An audit is already running.");
      return;
    }
    setStatus({ status: "running" });
    toast.success("Audit started — every page in the sitemap is being fetched.");
    poll.current = setInterval(async () => {
      const { data: s } = await api.get("/admin/seo-audit/status");
      setStatus(s);
      if (s.status === "done" || s.status === "failed") {
        clearInterval(poll.current);
        if (s.status === "failed") toast.error(s.error || "The audit failed.");
        else toast.success("Audit finished.");
        loadAudit();
        loadRest();
      }
    }, 4000);
  };

  const runIndexNow = async () => {
    const { data } = await api.post("/admin/indexnow/run");
    toast[data.ok ? "success" : "error"](
      data.ok ? `${data.submitted || 0} URL(s) submitted, ${data.changed} changed since the last run.`
        : data.reason || data.detail || "Submission failed."
    );
    loadRest();
  };

  const s = audit?.summary || {};

  return (
    <div className="space-y-6" data-testid="seo-audit-panel">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[260px]">
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Origin to audit</label>
          <Input
            value={origin}
            onChange={(e) => setOrigin(e.target.value)}
            placeholder="https://cassonline.co.uk (default)"
            data-testid="seo-audit-origin"
          />
          <p className="text-xs text-slate-500 mt-1">
            Audits what a crawler is actually served. Leave blank for the live site — a preview URL will report the
            unbuilt shell, which is not what Google sees.
          </p>
        </div>
        <Button onClick={startRun} disabled={status.status === "running"} data-testid="seo-audit-run">
          {status.status === "running" ? <Loader2 className="animate-spin mr-2" size={16} /> : <Play className="mr-2" size={16} />}
          {status.status === "running" ? "Auditing…" : "Run the audit"}
        </Button>
        <Button variant="outline" onClick={() => { loadAudit(); loadRest(); }} data-testid="seo-audit-refresh">
          <RefreshCw size={16} className="mr-2" /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={16} /> Loading…</div>
      ) : !audit ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-6 text-slate-600" data-testid="seo-audit-empty">
          No audit has been run yet. The first run becomes the baseline everything else is compared against.
        </div>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Card label="Issues found" value={s.issues ?? 0} hint={`${s.P0 || 0} critical · ${s.P1 || 0} important · ${s.P2 || 0} worth doing`} testid="seo-audit-total" />
            <Card label="URLs audited" value={audit.urls_audited} hint={`${s.ok_pages || 0} returned 200`} testid="seo-audit-urls" />
            <Card label="Pages with no title" value={s.missing_title ?? 0} hint="In the HTML, before JavaScript runs" testid="seo-audit-missing-title" />
            <Card label="Thin pages" value={s.thin_pages ?? 0} hint="Under 250 words of readable text" testid="seo-audit-thin" />
          </div>
          <p className="text-xs text-slate-500">
            Run {audit.at?.slice(0, 16).replace("T", " ")} against {audit.origin}
            {audit.is_baseline ? " — this is the baseline." : ""} Median response {s.median_ms || 0}ms.
          </p>

          {coverage && (
            <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="seo-coverage">
              <h3 className="font-semibold text-navy">Crawler-readable content</h3>
              <p className="text-sm text-slate-600 mt-1">
                {coverage.server_rendered} pages are built by the API, {coverage.rendered_at_build} location pages are
                built from their data files, out of {coverage.sitemap_urls} URLs in the sitemap.
                {coverage.not_covered_count > 0
                  ? ` ${coverage.not_covered_count} still get the plain shell.`
                  : " Every URL in the sitemap is covered."}
              </p>
              {coverage.not_covered_count > 0 && (
                <ul className="text-xs text-slate-500 mt-2 grid sm:grid-cols-2 gap-x-6">
                  {coverage.not_covered.slice(0, 20).map((p) => <li key={p}>{p}</li>)}
                </ul>
              )}
            </div>
          )}

          {indexnow && (
            <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="indexnow-status">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="font-semibold text-navy">Telling search engines about changes</h3>
                  <p className="text-sm text-slate-600 mt-1">
                    {indexnow.configured
                      ? <>IndexNow is set up and tracking {indexnow.urls_tracked} URLs. Last run {indexnow.last_run ? indexnow.last_run.slice(0, 16).replace("T", " ") : "not yet"}{indexnow.last_changed ? `, ${indexnow.last_changed} changed` : ""}. New and updated pages are submitted automatically every hour.</>
                      : "No IndexNow key is configured on the server yet."}
                  </p>
                  {indexnow.configured && (
                    <p className="text-xs text-slate-500 mt-1">Key file: {indexnow.key_location}</p>
                  )}
                </div>
                <Button variant="outline" onClick={runIndexNow} data-testid="indexnow-run">
                  <Send size={16} className="mr-2" /> Submit changes now
                </Button>
              </div>
              {(indexnow.recent || []).length > 0 && (
                <ul className="text-xs text-slate-500 mt-3 space-y-1">
                  {indexnow.recent.slice(0, 5).map((r, i) => (
                    <li key={i}>
                      {r.at?.slice(0, 16).replace("T", " ")} — {r.ok ? "accepted" : "failed"} ({r.status}), {r.submitted} URL(s)
                      {r.detail ? ` — ${r.detail}` : ""}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {compare?.available && (
            <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="seo-audit-compare">
              <h3 className="font-semibold text-navy">Against the previous run</h3>
              <p className="text-sm text-slate-600 mt-1">
                {compare.issues_now} issues now, {compare.issues_before} before
                ({compare.issues_now - compare.issues_before >= 0 ? "+" : ""}{compare.issues_now - compare.issues_before}).
              </p>
              <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1 text-sm">
                {compare.rows.filter((r) => r.change !== 0).map((r) => (
                  <div key={r.code} className="flex justify-between">
                    <span className="text-slate-600">{r.code}</span>
                    <span className={r.change < 0 ? "text-emerald-700 font-semibold" : "text-red-700 font-semibold"}>
                      {r.change > 0 ? "+" : ""}{r.change}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            {["", "P0", "P1", "P2"].map((p) => (
              <button
                key={p || "all"}
                onClick={() => setPriority(p)}
                data-testid={`seo-audit-filter-${p || "all"}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${priority === p ? "bg-navy text-white border-navy" : "bg-white text-slate-600 border-slate-200"}`}
              >
                {p === "" ? "All priorities" : p}
              </button>
            ))}
            <select
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="text-sm border border-slate-200 rounded-md px-2 py-1.5"
              data-testid="seo-audit-code-filter"
            >
              <option value="">All issue types</option>
              {Object.entries(s.by_code || {}).map(([c, n]) => (
                <option key={c} value={c}>{c} ({n})</option>
              ))}
            </select>
            <span className="text-xs text-slate-500">{audit.issue_count} shown</span>
          </div>

          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="min-w-full text-sm" data-testid="seo-audit-issues">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2">Priority</th>
                  <th className="text-left px-3 py-2">Issue</th>
                  <th className="text-left px-3 py-2">URL</th>
                  <th className="text-left px-3 py-2">What was found</th>
                  <th className="text-left px-3 py-2">Recommended fix</th>
                </tr>
              </thead>
              <tbody>
                {(audit.issues || []).map((i, idx) => (
                  <tr key={idx} className="border-t border-slate-100 align-top">
                    <td className="px-3 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold border ${PRIORITY_STYLE[i.priority]}`}>
                        {i.priority}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-medium text-navy whitespace-nowrap">{i.code}</td>
                    <td className="px-3 py-2">
                      <a className="text-orange hover:underline" href={`${audit.origin}${i.path}`} target="_blank" rel="noreferrer">{i.path}</a>
                    </td>
                    <td className="px-3 py-2 text-slate-600">{i.detail}</td>
                    <td className="px-3 py-2 text-slate-600">{i.fix}</td>
                  </tr>
                ))}
                {(audit.issues || []).length === 0 && (
                  <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-500">
                    <CheckCircle2 className="inline mr-2 text-emerald-600" size={16} /> Nothing matching that filter.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          {runs.length > 1 && (
            <p className="text-xs text-slate-500" data-testid="seo-audit-runs">
              {runs.length} runs kept: {runs.map((r) => `${r.at?.slice(0, 10)} (${r.issues})`).join(" · ")}
            </p>
          )}

          {s.P0 > 0 && (
            <p className="text-sm text-red-700 flex items-center gap-2">
              <AlertTriangle size={16} /> Critical issues block indexing or lose a page outright — those first.
            </p>
          )}
        </>
      )}
    </div>
  );
}
